import React, { useState, useEffect } from 'react';
import queryString from 'query-string';
import axios from 'axios';

import BasePageUAM from '../BasePageUAM/BasePageUAM';
import { PrsButtonLg } from '../../styles-and-themes/styles/prsButtons';
import {
  UamHeading,
  UamBodyText,
  UamFlexColumn,
  UamLoadingContainer,
  UamPageIcon,
} from '../../styles-and-themes/styles/uam-registration-styles';
import ProseSpinner from '../../components/ProseSpinner/ProseSpinner';
import PrsResetGlobalStyle from '../../styles-and-themes/styles/prsReset';
import { getFullNameFromShortName, getShortProductName } from '../../../shared-with-server/product-name-utils';
import { destinationInfo, frontEndUrl } from '../../destructured-front-end-config';
import { composeDestinationQuerystring, getReturnUrlByDestination } from '../../../shared-with-server/destination-utils';
import { isJwtValid, safeDecodeJwt } from '../../utils/jwt-utils';

const STEP_LOADING = 'loading';
const STEP_SUCCESS = 'success';
const STEP_EXPIRED = 'expired';

const REDIRECT_DELAY_MS = 1500;

const CHECK_CIRCLE_ICON_URL = `${frontEndUrl}static/images/check_circle.svg`;
const GPP_MAYBE_ICON_URL = `${frontEndUrl}static/images/gpp_maybe.svg`;

const LoginVerifyPage = () => {
  const [encodedKey] = useState(() => queryString.parse(location.search)?.key);
  const [keyIsValid] = useState(() => isJwtValid(encodedKey));

  const [destPath] = useState(() => {
    const decoded = safeDecodeJwt(encodedKey) || {};
    const ctx = decoded.loginContext || {};
    return { dest: ctx.dest, path: ctx.path, retURL: ctx.retURL || '/' };
  });

  const getBrandInfo = () => {
    const shortProductName = getShortProductName(destinationInfo, location.host, destPath);
    return { short: shortProductName, full: getFullNameFromShortName(shortProductName) };
  };
  const [brand] = useState(getBrandInfo);

  const [step, setStep] = useState(() => (keyIsValid ? STEP_LOADING : STEP_EXPIRED));

  const loginUrl = `/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`;

  useEffect(() => {
    if (!keyIsValid) return undefined;

    const controller = new AbortController();

    axios.post(
      '/api/v2/fi/token/email-verify',
      { key: encodedKey },
      { validateStatus: (s) => s < 500 || s >= 600, signal: controller.signal },
    ).then((res) => {
      if (res.status === 200 && !res.data?.subStatus) {
        setStep(STEP_SUCCESS);
        const target = res.data?.redirect
          ? getReturnUrlByDestination(destinationInfo, res.data.redirect.dest, res.data.redirect.path)
            || res.data.redirect.retURL
            || '/'
          : '/';
        setTimeout(() => { window.location.href = target; }, REDIRECT_DELAY_MS);
        return;
      }
      // Registration / agreement / unknown — punt to sign-in.
      setStep(STEP_EXPIRED);
    }).catch((err) => {
      if (axios.isCancel(err)) return;
      setStep(STEP_EXPIRED);
    });

    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  let content;
  if (step === STEP_LOADING) {
    content = (
      <UamLoadingContainer>
        <ProseSpinner size="5rem" />
      </UamLoadingContainer>
    );
  } else if (step === STEP_SUCCESS) {
    content = (
      <UamFlexColumn gap="16px">
        {/* TODO: swap to design's mark_email_read icon once asset lands. */}
        <UamPageIcon src={CHECK_CIRCLE_ICON_URL} alt="" />
        <UamHeading>Email verified.</UamHeading>
        <UamBodyText>Your identity has been confirmed. Signing you in now.</UamBodyText>
      </UamFlexColumn>
    );
  } else {
    content = (
      <UamFlexColumn gap="16px">
        <UamPageIcon src={GPP_MAYBE_ICON_URL} alt="" />
        <UamHeading>Verification link no longer valid.</UamHeading>
        <UamBodyText>
          This verification link has expired or has already been used. Please return to sign in to start over.
        </UamBodyText>
        <PrsButtonLg
          type="button"
          style={{ width: '100%', marginTop: '8px' }}
          onClick={() => { window.location.href = loginUrl; }}
        >
          Back To Sign In
        </PrsButtonLg>
      </UamFlexColumn>
    );
  }

  return (
    <>
      {/* Page-scoped reset; lift to App.js once verified safe across all pages. */}
      <PrsResetGlobalStyle />
      <BasePageUAM
        brand={brand}
        destPath={destPath}
        showTabs={false}
      >
        {content}
      </BasePageUAM>
    </>
  );
};

export default LoginVerifyPage;
